import { useCallback, useEffect } from "react";
import * as React from "react";
import {
  DeepMap,
  FieldError,
  FieldValues,
  UseControllerProps,
  useController
} from "react-hook-form";

import { grey } from "@mui/material/colors";
import type { SxProps } from "@mui/material/styles";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";

import { format, startOfDay } from "date-fns";
import { get, merge } from "lodash";

import { isDateStringValid } from "utils/common";

type DateFormFieldProps = {
  name: string;
  label?: string;
  disabled?: boolean;
  required?: boolean;
  inputFormat?: string;
  PopperProps?: any;
  sx?: SxProps;
  showBorder?: boolean;
  disablePast?: boolean;
  // Earliest selectable calendar day. Also rejects an earlier date typed by hand.
  minDate?: Date;
  onChange?: (value: Date | null) => void;
  // Caller-supplied check composed AFTER the built-in required/valid/minDate rules, so a form
  // can refuse a date live (e.g. month-end locked periods) with the error on the field itself.
  extraValidate?: (value: unknown) => true | string;
};

export type RhfDateFormFieldProps<T extends FieldValues> = DateFormFieldProps &
  UseControllerProps<T>;

export default function DateFormField<T extends FieldValues>({
  name,
  label = "",
  disabled = false,
  control,
  inputFormat,
  required = false,
  PopperProps,
  sx = { ml: 2, mt: 1 },
  showBorder,
  disablePast = false,
  minDate,
  onChange,
  extraValidate
}: RhfDateFormFieldProps<T>) {
  const validate = useCallback(
    (value: any) => {
      if (disabled) return true;
      if (required && !value) {
        return `${label} is required.`;
      }
      if (value && !isDateStringValid(value)) return `${label} is not valid.`;
      if (value && minDate && new Date(value) < startOfDay(minDate)) {
        return `${label} must be on or after ${format(minDate, "MMM d, yyyy")}.`;
      }
      if (value && extraValidate) return extraValidate(value);
      return true;
    },
    [required, disabled, label, minDate, extraValidate]
  );
  // react-hook-form registers rules ONCE (useController keeps them in a never-updated ref,
  // verified against 7.56.4), so a validate closure registered on the first render is frozen —
  // extraValidate over data that loads after mount (locked accounting periods) would silently
  // never fire. Register a stable wrapper that reads the latest closure through a ref instead.
  // The ref is updated in an effect (never during render — concurrent-mode rule); validation
  // only runs on user events, which always follow the commit, so it still reads the latest.
  const validateRef = React.useRef(validate);
  useEffect(() => {
    validateRef.current = validate;
  });
  const {
    field,
    formState: { errors }
  } = useController<T>({
    name,
    control,
    rules: {
      validate: (value: unknown) => validateRef.current(value)
    }
  });
  const errorMessage =
    get(errors, name) &&
    `${(get(errors, name) as DeepMap<FieldValues, FieldError>).message}`;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <DesktopDatePicker
        views={["year", "month", "day"]}
        disabled={disabled}
        disablePast={disablePast}
        minDate={minDate}
        closeOnSelect
        {...(inputFormat ? { inputFormat: inputFormat } : {})}
        {...field}
        onChange={(value) => {
          if (isDateStringValid(value) || !value) {
            field.onChange(value);
            onChange?.(value);
          }
        }}
        value={field.value ? new Date(field.value) : null}
        slotProps={{
          popper: PopperProps,
          textField: {
            required,
            disabled,
            fullWidth: true,
            label,
            InputLabelProps: { disabled },
            InputProps: { disableUnderline: disabled },
            error: !disabled && Boolean(errorMessage),
            helperText: disabled ? "" : errorMessage || "",

            sx: merge(
              sx,
              showBorder && disabled
                ? { borderBottom: `solid 1px ${grey[500]}` }
                : {}
            )
          }
        }}
      />
    </LocalizationProvider>
  );
}
